const sectionStyle = {
  maxWidth: "1100px",
  margin: "0 auto",
  padding: "60px 20px"
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "20px"
};

const cardStyle = {
  border: "1px solid #eee",
  borderRadius: "12px",
  padding: "20px",
  background: "#fff",
  transition: "all 0.2s ease"
};

const hoverEffect = (e, enter = true) => {
  e.currentTarget.style.boxShadow = enter
    ? "0 10px 25px rgba(0,0,0,0.08)"
    : "none";
  e.currentTarget.style.transform = enter
    ? "translateY(-2px)"
    : "translateY(0)";
};

const ProductionFocused = () => {
  return (
    <div>

      {/* HERO */}
      <div style={{ ...sectionStyle, paddingTop: "80px" }}>
        <h1 style={{ fontSize: "42px", marginBottom: "20px" }}>
          Production-Focused Engineering
        </h1>

        <p style={{ fontSize: "20px", color: "#555", maxWidth: "700px" }}>
          I design and build systems with production in mind from day one—
          focusing on scalability, reliability, security, and long-term maintainability.
        </p>
      </div>


      {/* WHAT THIS MEANS */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "30px" }}>
          What This Means for Your System
        </h2>

        <div style={gridStyle}>
          {[
            "Systems designed to handle real production workloads",
            "Built-in resilience and fault tolerance",
            "Monitoring and observability from the start",
            "Security integrated into architecture, not added later",
            "Cost-aware design decisions",
            "Maintainable systems that evolve over time"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* CORE PRINCIPLES */}
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: "30px" }}>Core Engineering Principles</h2>

        <div style={gridStyle}>
          {[
            {
              title: "Scalability by Design",
              text: "Systems are designed to scale horizontally and handle growth without major rework."
            },
            {
              title: "Reliability & Fault Tolerance",
              text: "Failures are expected and handled gracefully through retries, fallbacks, and redundancy."
            },
            {
              title: "Observability",
              text: "Logging, metrics, and tracing provide full visibility into system behavior."
            },
            {
              title: "Security by Design",
              text: "IAM, encryption, and secure patterns are built into the system from the beginning."
            },
            {
              title: "Cost Awareness",
              text: "Architectures are optimized to avoid unnecessary AWS costs while maintaining performance."
            },
            {
              title: "Simplicity Over Complexity",
              text: "Solutions are kept as simple as possible to reduce technical debt and improve maintainability."
            }
          ].map((principle, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              <h3 style={{ marginBottom: "10px" }}>{principle.title}</h3>
              <p style={{ color: "#555" }}>{principle.text}</p>
            </div>
          ))}
        </div>
      </div>


      {/* WHAT I IMPLEMENT */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "30px" }}>What I Implement in Practice</h2>

        <div style={gridStyle}>
          {[
            "Event-driven architectures for scalability and decoupling",
            "Retry strategies, dead-letter queues, and failure handling",
            "Centralized logging and monitoring (CloudWatch, X-Ray)",
            "CI/CD pipelines with automated testing and deployment",
            "Infrastructure as Code for consistency and repeatability",
            "Secure access patterns using IAM and secrets management",
            "Performance optimization across application and infrastructure layers",
            "Load handling and scaling strategies"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* COMMON MISTAKES */}
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: "30px" }}>Common Mistakes I Help Avoid</h2>

        <div style={gridStyle}>
          {[
            "Systems that work in development but fail in production",
            "Lack of monitoring and debugging capabilities",
            "Over-engineered architectures that are hard to maintain",
            "Ignoring security until late stages",
            "Uncontrolled AWS costs due to poor design",
            "Tightly coupled systems that are difficult to scale",
            "Manual deployments and lack of automation"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* WHY IT MATTERS */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "30px" }}>Why It Matters</h2>

        <div style={gridStyle}>
          {[
            "Reduces risk of failures in production",
            "Improves system reliability and uptime",
            "Ensures predictable performance under load",
            "Minimizes long-term maintenance costs",
            "Supports business growth without re-architecture",
            "Provides full visibility into system behavior"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* FINAL CTA */}
      <div style={{
        textAlign: "center",
        margin: "100px 0",
        padding: "60px 20px",
        background: "#fafafa"
      }}>
        <h2 style={{
          fontSize: "32px",
          marginBottom: "16px"
        }}>
          Build Systems Ready for Production
        </h2>

        <p style={{
          color: "#555",
          fontSize: "18px",
          marginBottom: "32px",
          maxWidth: "600px",
          marginLeft: "auto",
          marginRight: "auto",
          lineHeight: "1.6"
        }}>
          Let’s design and build a system that performs reliably in real-world conditions.
        </p>

        <a href="/contact" style={{
          display: "inline-block",
          padding: "16px 36px",
          background: "#007bff",
          color: "#fff",
          borderRadius: "8px",
          textDecoration: "none",
          fontWeight: "500",
          fontSize: "16px",
          transition: "all 0.2s ease",
          boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
        }}
        >
          Start a Project
        </a>
      </div>
      

    </div>
  );
};

export default ProductionFocused;